import {
	getSelectCommonProject,
	getUncsCommonProject,
	getUncTechnicalSpecificationsCommonProject,
} from '@root-gipro/modules/commonProjects/store/actions'
import { fetchUncsArchive, fetchUserProjectArchive } from '@root-gipro/modules/userArchive/store/actions'
import { UserProjectToolbar } from '@root-gipro/modules/userProjects/components/Project/CheckPlate/components/UserProjectToolbar'
import { fetchUncs, fetchUserProject, getProjectTechSolutions } from '@root-gipro/modules/userProjects/store/actions'
import { Routes } from '@root-gipro/router'
import { BreadcrumbsComponent } from '@root-gipro/shared/BreadcrumbsComponent/BreadcrumbsComponent'
import { usePageLink } from '@root-gipro/shared/hooks/usePageLink'
import { TypesNavLink } from '@root-gipro/shared/TabsComponents/tabs.props'
import { IState } from '@root-gipro/store/interfaces/state'
import { searchByKey, timestampToDate } from '@root-gipro/utils/helpers/common'
import React, { FC, memo, MutableRefObject, useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation } from 'react-router-dom'

const SelectedContainer: FC = memo(({ children }) => {
	const dispatch = useDispatch()
	const { pathname } = useLocation()
	const { pathRoute, nameLink } = usePageLink()

	const urlId = useMemo(() => pathname.replace(/[^\d]/g, ''), [pathname])
	const currentState = useMemo(() => (pathRoute.includes('user-archive') ? 'userArchives' : 'userProjects'), [
		pathRoute,
	])
	const isCommonProjects = pathRoute === Routes.COMMON_PROJECTS
	const isUserProjects = pathRoute === Routes.USER_PROJECTS
	const isUserArchive = pathRoute === Routes.USER_ARCHIVE

	const { selectedProject, isProjectLoadedFromCard } = useSelector((state: IState) => state.commonProjects)
	const { selectedPurchase } = useSelector((state: IState) => state.purchases)
	const { groups, company } = useSelector((state: IState) => state.public)
	const { versionIpr: versionIprInAdminPanel } = useSelector((state: IState) => state.adminPanel)
	const { originalObj, projectObj } = useSelector((state: IState) => state[currentState])

	const prevUncSortRef = useRef<any>()

	const routes: any = useMemo(
		() => ({
			parameters: {
				[Routes.COMMON_PROJECTS]: Routes.COMMON_PROJECTS_PARAMETERS.replace(/:id/, urlId),
				[Routes.USER_PROJECTS]: Routes.USER_PROJECTS_PARAMETERS.replace(/:id/, urlId),
				[Routes.USER_ARCHIVE]: Routes.USER_ARCHIVE_PARAMETERS.replace(/:id/, urlId),
			},
			rates: {
				[Routes.COMMON_PROJECTS]: Routes.COMMON_PROJECTS_RATES.replace(/:id/, urlId),
				[Routes.USER_PROJECTS]: Routes.USER_PROJECTS_RATES.replace(/:id/, urlId),
				[Routes.USER_ARCHIVE]: Routes.USER_ARCHIVE_RATES.replace(/:id/, urlId),
			},
			tech: {
				[Routes.COMMON_PROJECTS]: Routes.COMMON_PROJECTS_TECHNICAL_SOLUTIONS.replace(/:id/, urlId),
				[Routes.USER_PROJECTS]: Routes.USER_PROJECTS_TECHNICAL_SOLUTIONS.replace(/:id/, urlId),
				[Routes.USER_ARCHIVE]: Routes.USER_ARCHIVE_TECHNICAL_SOLUTIONS.replace(/:id/, urlId),
			},
		}),
		[urlId]
	)

	const typesRoute: TypesNavLink[] = useMemo(() => {
		return [
			{
				title: 'Параметры',
				path: routes.parameters[pathRoute],
				tags: [Routes.COMMON_PROJECTS, Routes.USER_PROJECTS, Routes.USER_ARCHIVE],
			},
			{
				title: 'Расценки',
				path: routes.rates[pathRoute],
				tags: [Routes.COMMON_PROJECTS, Routes.USER_PROJECTS, Routes.USER_ARCHIVE],
			},
			{
				title: 'Технические решения',
				path: routes.tech[pathRoute],
				tags: [Routes.COMMON_PROJECTS, Routes.USER_PROJECTS, Routes.USER_ARCHIVE],
			},
		]
	}, [routes, pathRoute])

	const versionIpr = useMemo(() => {
		if (projectObj.companyId) {
			return company.find(c => c.id === projectObj.companyId)?.ipr?.find((i: any) => i.id === projectObj.ipr_id)
		}
		return { version_name: versionIprInAdminPanel }
	}, [company, projectObj.companyId, projectObj.ipr_id, versionIprInAdminPanel])

	const { author, authorTel } = useMemo(() => {
		const { author } = projectObj
		if (!author) return { author: '', authorTel: '' }

		const fullName = `${author.firstname} ${author.lastname}`
		const phone = JSON.parse(author.data || '{}').phone || ''

		return { author: fullName, authorTel: phone }
	}, [projectObj.author])

	const group = useMemo(() => {
		if (pathname === routes.parameters[Routes.COMMON_PROJECTS] && groups.length && selectedProject?.groupId) {
			return searchByKey(groups, 'id', selectedProject.groupId)
		}
		return undefined
	}, [pathname, routes.parameters, selectedProject?.groupId, groups])

	useEffect(() => {
		if (urlId === '0') return

		if (isUserProjects) {
			dispatch(fetchUserProject(urlId))
			dispatch(fetchUncs(urlId))
		} else if (isUserArchive) {
			dispatch(fetchUserProjectArchive(urlId))
			dispatch(fetchUncsArchive(urlId))
		}
	}, [urlId, isUserProjects, isUserArchive, dispatch])

	useEffect(() => {
		const matchesTab = ['parameters', 'rates', 'construction-resources'].some(p => pathname.includes(p))
		if (isCommonProjects && matchesTab && urlId && groups.length && !selectedProject?.groupId) {
			if (!isProjectLoadedFromCard) dispatch(getSelectCommonProject(urlId))
			dispatch(getUncsCommonProject(urlId))
			dispatch(getUncTechnicalSpecificationsCommonProject(urlId))
		}
	}, [pathname, isCommonProjects, groups.length, selectedProject?.groupId, isProjectLoadedFromCard, urlId, dispatch])

	return (
		<>
			{pathRoute === Routes.USER_PROJECTS || pathRoute === Routes.USER_ARCHIVE ? (
				<UserProjectToolbar
					projectObj={projectObj}
					versionIpr={versionIpr}
					author={author}
					authorTel={authorTel}
					timestampToDate={timestampToDate}
					originalObj={originalObj}
					typePageUrl={pathname}
				/>
			) : (
				<div className='container'>
					<BreadcrumbsComponent
						typesRoute={typesRoute as TypesNavLink[]}
						group={groups[group as number] || originalObj}
						path={pathRoute && pathRoute}
						nameLink={nameLink}
						urlId={urlId}
					/>
				</div>
			)}
			{children}
		</>
	)
})
export default SelectedContainer
